// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="FormDisplayField" set="mytaible-shadcn-studio" verified=tbd

import { Check, Copy } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FormDisplayFieldProps {
  /** Field label — same visual weight as FormLabel */
  label: string;
  /**
   * The display value. Accepts any ReactNode — pass a `<Badge>`, formatted
   * date string, or any other content. When omitted or empty, `empty` is shown.
   */
  value?: React.ReactNode;
  /**
   * Placeholder shown when `value` is `undefined`, `null`, or an empty string.
   * Default: "—"
   */
  empty?: string;
  /**
   * Renders the value in a monospaced font.
   * Useful for IDs, tokens, and code values.
   */
  mono?: boolean;
  /**
   * When provided, appends a copy-to-clipboard button next to the value.
   * Only shown when the value is non-empty. Pass the raw string to copy
   * (may differ from the displayed ReactNode).
   */
  copyValue?: string;
  /** Optional hint text rendered below the value — mirrors FormDescription. */
  description?: string;
  /**
   * Layout variant for the label/value pair.
   * - `stacked` (default): label above value — matches form input rhythm, use
   *   inside multi-column FormSection grids.
   * - `horizontal`: label on the left, value on the right — compact single-row
   *   style suited for read-only settings lists.
   */
  layout?: 'stacked' | 'horizontal';
  className?: string;
}

// ─── CopyButton ───────────────────────────────────────────────────────────────

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);

  function handleCopy() {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? 'Copied' : 'Copy to clipboard'}
      className="ml-1.5 shrink-0 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:rounded-sm"
    >
      {copied ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
    </button>
  );
}

// ─── FormDisplayField ─────────────────────────────────────────────────────────

/**
 * Read-only field that renders a label + value in the same visual rhythm as a
 * form input field. Drop it directly into a FormSection grid alongside editable
 * fields, or use it when `FormLayout formState="view"` is active.
 *
 * @example
 * // Alongside edit fields in a mixed read/write section
 * <FormSection columns={2}>
 *   <FormDisplayField label="Created by" value={record.createdBy} />
 *   <FormDisplayField label="Client ID" value={app.clientId} mono copyValue={app.clientId} />
 * </FormSection>
 *
 * @example
 * // Status with a Badge value
 * <FormDisplayField label="Status" value={<Badge variant="success" appearance="soft">Active</Badge>} />
 */
export function FormDisplayField({
  label,
  value,
  empty = '—',
  mono = false,
  copyValue,
  description,
  layout = 'stacked',
  className,
}: FormDisplayFieldProps) {
  const isEmpty = value === undefined || value === null || value === '';
  const showCopy = !isEmpty && copyValue !== undefined && copyValue !== '';

  if (layout === 'horizontal') {
    return (
      <div className={cn('flex items-center justify-between gap-4 py-2', className)}>
        <p className="shrink-0 text-sm font-medium leading-none text-foreground">{label}</p>
        <div className="flex items-center gap-1.5">
          {isEmpty ? (
            <span className="text-sm text-muted-foreground">{empty}</span>
          ) : (
            <span className={cn('text-sm text-foreground', mono && 'font-mono')}>{value}</span>
          )}
          {showCopy && <CopyButton value={copyValue ?? ''} />}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Label — matches FormLabel: text-sm font-medium */}
      <p className="text-sm font-medium leading-none text-foreground">{label}</p>

      {/* Value area — min-h-9 matches standard input height to preserve grid rhythm */}
      <div className="flex min-h-9 items-center">
        {isEmpty ? (
          <span className="text-sm text-muted-foreground">{empty}</span>
        ) : (
          <span className={cn('text-sm text-foreground break-words', mono && 'font-mono')}>
            {value}
          </span>
        )}
        {showCopy && <CopyButton value={copyValue ?? ''} />}
      </div>

      {/* Description — mirrors FormDescription */}
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
  );
}

FormDisplayField.displayName = 'FormDisplayField';
